<script setup lang="ts">
import type { CmsElementForm } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { useVuelidate } from "@vuelidate/core";
import type { ValidationRuleWithoutParams } from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import { defu } from "defu";
import { computed, reactive, ref } from "vue";
import { useCmsElementConfig, useNewsletter, useSalutations } from "#imports";
const { resolveApiErrors, scrollToError } = useApiErrorsResolver("account_register");
const { pushError } = useNotifications();


type Translations = {
  form: {
    subscribeLabel: string;
    unsubscribeLabel: string;
    action: string;
    email: string;
    emailPlaceholder: string;
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    privacy: string;
    privacyLabel: string;
    submit: string;
    newsletterBenefits: string;
  };
};

let translations: Translations = {
  form: {
    subscribeLabel: "Subscribe to newsletter",
    unsubscribeLabel: "Unsubscribe from newsletter",
    action: "Action",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    privacy: "Privacy",
    privacyLabel: "I have read the data protection information.",
    submit: "Submit",
    newsletterBenefits:
      "Be aware of upcoming sales and events.Receive gifts and special offers!",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const errorMessages = ref<ApiError[]>([]);
const subscriptionOptions: {
  label: string;
  value: "subscribe" | "unsubscribe";
}[] = [
  {
    label: translations.form.subscribeLabel,
    value: "subscribe",
  },
  {
    label: translations.form.unsubscribeLabel,
    value: "unsubscribe",
  },
];
const { getSalutations } = useSalutations();
const { newsletterSubscribe, newsletterUnsubscribe } = useNewsletter();

const state = reactive({
  option: subscriptionOptions[0].value,
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  checkbox: true,
  securityFormField: "",
});

watch(getSalutations, (newValue) => {
  state.salutationId = newValue[newValue.length - 1].id;
});

type Rules = {
  email: {
    required: ValidationRuleWithoutParams;
    email: ValidationRuleWithoutParams;
  };
  checkbox: {
    isTrue: (value: boolean) => boolean;
  };
  firstName: {
    minLength: number;
  };
  lastName: {
    minLength: number;
  };
};
const rules = computed(() => {
  let temp: Partial<Rules> = {
    email: {
      required,
      email,
    },
    checkbox: {
      isTrue: (value: boolean) => value === true,
    },
  };
  if (state.option === "subscribe") {
    temp = {
      ...temp,
      firstName: {
        minLength: 3,
      },
      lastName: {
        minLength: 3,
      },
    };
  }
  return temp;
});

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    loading.value = true;
    try {
      if (state.option === "subscribe") {
        await newsletterSubscribe({
          ...state,
        });
      } else {
        await newsletterUnsubscribe(state.email);
      }
      formSent.value = true;
    } catch (error) {
      errorMessages.value = resolveApiErrors(error.details.errors);
        for (const error of errorMessages.value) {
          pushError(error);
        }
    } finally {
      loading.value = false;
    }
  }
};

</script>
<template>
  <div class="newsletter-form">
    <div class="container">
      <div class="newsletter py-16 max-w-[520px] 3xl:max-w-[700px] mx-auto text-center flex flex-col gap-4 md:gap-6">
        <div class="headline px-0 2xl:px-[40px] 3xl:px-[80px]">
          <h3 class="h3 text-2xl md:text-4xl lg:text-8xl 3xl:text-10xl font-normal leading-none text-primary">
            {{ $t("footerNewsletterForm.mainTitle") }}
          </h3>
          <p class="text-base 3xl:text-md mt-4 font-normal">
            {{ $t("footerNewsletterForm.description") }}
          </p>
        </div>
        <form class="form-inline flex flex-row gap-0 mt-3 relative" @submit.prevent="invokeSubmit">
          <div v-if="loading"
            class="absolute inset-0 flex items-center justify-center z-10 bg-white/50"
          >
            <div
              class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
            />
          </div>

          <template v-if="!formSent">
            <input type="hidden" name="securityFormField" v-model="state.securityFormField" />
            <label for="newsletter-footer-email-address" class="sr-only">
              {{ $t('footerNewsletterForm.placeholder') }}
            </label>
            <input
              id="newsletter-footer-email-address"
              v-model="state.email"
              name="email"
              type="email"
              autocomplete="email"
              :class="[
              $v.email?.$error
                ? 'border-red focus:border-red'
                : 'border-gray-300 focus:border-primary',
              ]"
              class="form-control rounded-none text-base sm:text-md bg-transparent pr-4 w-full input-primary border-b-2 border-primary placeholder:text-grey-700 font-medium"
              :placeholder="$t('footerNewsletterForm.placeholder')"
              @blur="$v.email?.$touch()"
            />
            <span
              v-if="$v.email?.$error"
              class="pt-1 text-sm text-red focus:ring-brand-primary border-gray-300 absolute -bottom-6"
            >
              <template v-if="$v.email?.$errors[0].$uid === 'email-required' || $v.email?.$errors[0].$uid === 'email-email'">
                {{ $t('validations.email') }}
              </template>
              <template v-else>
                {{ $v.email?.$errors[0].$message }}
              </template>
            </span>
            <div
              v-if="errorMessages.length"
              class="pt-1 text-sm text-red focus:ring-brand-primary border-gray-300 absolute -bottom-6"
              role="alert"
            >
              <ul>
                <li v-for="error in errorMessages" :key="error">
                  {{ error }}
                </li>
              </ul>
            </div>
            <button
              class="btn btn-primary btn-icon-end -ml-6"
              type="submit"
              :disabled="loading"
            >
              <span class="my-auto"> {{ $t("footerNewsletterForm.submitButtonText") }}</span>
              <i v-if="!loading" class="icon-arrow-right" ></i>
              <i v-else class="icon-refresh animate-spin"></i>
            </button>

            <div class="hidden">
              <div class="col-span-12">
                <label for="option">{{ $t('form.action') }} *</label>
                <select
                  id="option"
                  v-model="state.option"
                  name="option"
                  class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:border-primary focus:outline-none focus:ring-primary focus:z-10 sm:text-sm"
                >
                  <option
                    v-for="subscription in subscriptionOptions"
                    :key="subscription.value"
                    :value="subscription.value"
                  >
                    {{ subscription.label }}
                  </option>
                </select>
              </div>
              <div v-if="state.option === 'subscribe'" class="col-span-4">
                <label for="salutation">{{ $t('form.salutation') }} *</label>
                <select
                  id="salutation"
                  v-model="state.salutationId"
                  name="salutation"
                  class="appearance-none relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-sm"
                  :class="[
                    $v.salutationId?.$error
                      ? 'border-red focus:border-red'
                      : 'border-gray-300 focus:border-primary',
                  ]"
                  @blur="$v.salutationId?.$touch()"
                >
                  <option disabled selected value="">
                    {{ translations.form.salutationPlaceholder }}
                  </option>
                  <option
                    v-for="salutation in getSalutations"
                    :key="salutation.id"
                    :value="salutation.id"
                  >
                    {{ salutation.displayName }}
                  </option>
                </select>
                <span
                  v-if="$v.salutationId?.$error"
                  class="pt-1 text-sm text-red focus:ring-brand-primary border-gray-300"
                >
                  {{ $v.salutationId?.$errors[0].$message }}
                </span>
              </div>
              <div v-if="state.option === 'subscribe'" class="col-span-4">
                <label for="first-name">{{ $t('form.firstName') }} *</label>
                <input
                  id="first-name"
                  v-model="state.firstName"
                  name="first-name"
                  type="text"
                  autocomplete="given-name"
                  class="appearance-none relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-sm"
                  :class="[
                    $v.firstName?.$error
                      ? 'border-red focus:border-red'
                      : 'border-gray-300 focus:border-primary',
                  ]"
                  :placeholder="translations.form.firstNamePlaceholder"
                  @blur="$v.firstName?.$touch()"
                />
                <span
                  v-if="$v.firstName?.$error"
                  class="pt-1 text-sm text-red focus:ring-brand-primary border-gray-300"
                >
                  {{ $v.firstName?.$errors[0].$message }}
                </span>
              </div>
              <div v-if="state.option === 'subscribe'" class="col-span-4">
                <label for="last-name">{{ translations.form.lastName }} *</label>
                <input
                  id="last-name"
                  v-model="state.lastName"
                  name="last-name"
                  type="text"
                  autocomplete="family-name"
                  class="appearance-none relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-sm"
                  :class="[
                    $v.lastName?.$error
                      ? 'border-red focus:border-red'
                      : 'border-gray-300 focus:border-primary',
                  ]"
                  :placeholder="translations.form.lastNamePlaceholder"
                  @blur="$v.lastName?.$touch()"
                />
                <span
                  v-if="$v.lastName?.$error"
                  class="pt-1 text-sm text-red focus:ring-brand-primary border-gray-300"
                >
                  {{ $v.lastName?.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12">
                <label>{{ translations.form.privacy }} *</label>
                <div class="flex gap-3 items-start">
                  <input
                    id="privacy"
                    v-model="state.checkbox"
                    name="privacy"
                    type="checkbox"
                    class="custom-checkbox mt-1 focus:ring-primary h-4 w-4 border text-indigo-600 rounded"
                    :class="[
                      $v.checkbox?.$error ? 'border-red' : 'border-gray-300',
                    ]"
                  />
                  <div>
                    <label
                      :class="[$v.checkbox?.$error ? 'text-red' : '']"
                      for="privacy"
                    >
                      {{ translations.form.privacyLabel }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-end mt-10">

            </div>
          </template>
          <template v-else>
            <p class="text-base 3xl:text-md mt-4 font-normal text-center w-full text-green">
              {{ $t('form.newsletterBenefits') }}
            </p>
          </template>
        </form>
      </div>
    </div>
  </div>
</template>
